import { isFiniteNumber, isRecord } from "../../utils/json-guards.js";
import { parseGeoPose } from "./geo-pose.js";
//#region ../src/ar/qr/qr-level.ts
/**
* QR level-file loader — Phase 6 / §8 of the QR-code detection & tracking plan.
*
* The printed QR encodes only a short URL; everything else lives in the level
* file fetched from that URL: the physical QR size (drives `solvePnP` + the size
* self-check), the QR's absolute geo pose (drives the synthetic GPS vote), and
* the AR content to instantiate. Keeping size/geo out of the QR keeps the
* printed code low-density and lets authors fix a mis-measured size or relocate
* without reprinting.
*
* This module fetches and DEFENSIVELY validates that external, user-authored
* document at the boundary (CLAUDE.md "write defensively"). The AR `content`
* format is an open question (plan §12) — it is carried through opaquely and
* NOT interpreted here; only the fields the pose + vote need are validated.
*/
/** Thrown when a fetched level file fails validation. */
var QrLevelValidationError = class extends Error {
	constructor(message) {
		super(`qr-level: ${message}`);
		this.name = "QrLevelValidationError";
	}
};
/**
* Validate the optional `qr.physicalSizeM`. When present it MUST be a positive
* number (a `0`/negative authored size is a bug, not a "measure it instead"
* signal). Returns `undefined` when omitted.
*/
function parsePhysicalSize(value) {
	if (value === void 0) return void 0;
	if (!isFiniteNumber(value) || value <= 0) throw new QrLevelValidationError("\"qr.physicalSizeM\" must be a positive number when present");
	return value;
}
/**
* Validate the optional `qr.geo`. When present every field is validated (a
* partial geo is a bug — it would silently place the vote wrong). Returns
* `undefined` when omitted. The rules live in `geo-pose.ts`, shared with
* the tour manifest; this wrapper only supplies the document path and the
* level's error type.
*/
function parseGeo(value) {
	if (value === void 0) return void 0;
	if (!isRecord(value)) throw new QrLevelValidationError("\"qr.geo\" must be an object when present");
	return parseGeoPose(value, {
		path: "qr.geo",
		fail: (message) => {
			throw new QrLevelValidationError(message);
		}
	});
}
/**
* Validate an already-parsed value as a {@link QrLevel}. Throws
* {@link QrLevelValidationError} with a descriptive message on any violation.
*/
function parseQrLevel(data) {
	if (!isRecord(data)) throw new QrLevelValidationError("level file must be a JSON object");
	if (!isFiniteNumber(data.version)) throw new QrLevelValidationError("missing/invalid \"version\"");
	if (!isRecord(data.qr)) throw new QrLevelValidationError("missing/invalid \"qr\"");
	const physicalSizeM = parsePhysicalSize(data.qr.physicalSizeM);
	const geo = parseGeo(data.qr.geo);
	const mintQuality = parseMintQuality(data.qr.mintQuality);
	return {
		version: data.version,
		qr: {
			...physicalSizeM !== void 0 ? { physicalSizeM } : {},
			...geo !== void 0 ? { geo } : {},
			...mintQuality !== void 0 ? { mintQuality } : {}
		},
		content: "content" in data ? data.content : void 0
	};
}
/** Validate the optional `qr.mintQuality` (see {@link QrMintQuality}). */
function parseMintQuality(value) {
	if (value === void 0) return void 0;
	if (!isRecord(value)) throw new QrLevelValidationError("\"qr.mintQuality\" must be an object when present");
	const quality = {};
	for (const [key, kind] of Object.entries(MINT_QUALITY_FIELDS)) {
		const raw = value[key];
		if (raw === void 0) continue;
		quality[key] = checkMintQualityField(key, raw, kind);
	}
	return quality;
}
/** How each `mintQuality` field is validated. */
const MINT_QUALITY_FIELDS = {
	gpsAccuracyM: "positive",
	alignmentSampleCount: "count",
	alignmentRmseM: "non-negative",
	mintedAtIso: "text",
	sightingCount: "count",
	detectionCount: "count",
	rotationSpreadDeg: "non-negative",
	translationSpreadM: "non-negative",
	physicalSizeSpreadM: "non-negative",
	alignmentGpsExtentM: "non-negative",
	headingUncertain: "flag"
};
/** Validate one present `mintQuality` field, or throw naming it. */
function checkMintQualityField(key, raw, kind) {
	if (kind === "flag") return checkMintQualityFlag(key, raw);
	return kind === "text" ? checkMintQualityText(key, raw) : checkMintQualityNumber(key, raw, kind);
}
function checkMintQualityFlag(key, raw) {
	if (typeof raw !== "boolean") mintQualityError(key, "true or false");
	return raw;
}
function mintQualityError(key, expected) {
	throw new QrLevelValidationError(`"qr.mintQuality.${key}" must be ${expected} when present`);
}
function checkMintQualityText(key, raw) {
	if (typeof raw !== "string" || raw.trim() === "") mintQualityError(key, "a non-empty string");
	return raw;
}
function checkMintQualityNumber(key, raw, kind) {
	if (!isFiniteNumber(raw)) mintQualityError(key, "a finite number");
	if (kind === "positive" && raw <= 0) mintQualityError(key, "a positive number");
	if (kind === "non-negative" && raw < 0) mintQualityError(key, "a non-negative number");
	if (kind === "count" && (raw < 0 || !Number.isInteger(raw))) mintQualityError(key, "a non-negative integer");
	return raw;
}
/**
* Serialize a {@link QrLevel} to the JSON document `parseQrLevel` reads —
* the writer half the schema never had (the authoring loop stands on the
* exported file being re-readable). The input is re-validated first so a
* programming error fails LOUD here instead of producing a broken file an
* author uploads and a visitor cannot open.
*/
function serializeQrLevel(level) {
	const validated = parseQrLevel(level);
	return JSON.stringify(validated, null, 2);
}
/**
* Fetch and validate a level file from `url`. Rejects with
* {@link QrLevelValidationError} on a non-OK response, non-JSON body, or a
* schema violation.
*/
async function fetchQrLevel(url, options = {}) {
	const fetchImpl = options.fetchImpl ?? globalThis.fetch;
	if (!fetchImpl) throw new QrLevelValidationError("no fetch implementation available");
	let response;
	try {
		response = await fetchImpl(url, { signal: options.signal });
	} catch (err) {
		throw new QrLevelValidationError(`fetch failed for ${url}: ${err instanceof Error ? err.message : String(err)}`);
	}
	if (!response.ok) throw new QrLevelValidationError(`fetch ${url} returned status ${response.status}`);
	let body;
	try {
		body = await response.json();
	} catch {
		throw new QrLevelValidationError(`response for ${url} was not valid JSON`);
	}
	return parseQrLevel(body);
}
//#endregion
export { QrLevelValidationError, fetchQrLevel, parseQrLevel, serializeQrLevel };
